import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import classNames from 'classnames';
import { HeaderRow } from '../../../components/table/HeaderRow';
import { Row } from '../../../components/table/Row';
import { StyledTable } from "../../StorageManager/components/table/TableStyled";
import { getSortedRowsBy } from '../../../utils';
import EmailTagsModal from './EmailTagsModal';
import ViewEmailContentModal from './ViewEmailContentModal';
import { loadWorkOrderList, selectAssigneeForEmail, addTagForEmail, removeTagForEmail, loadEmailChildren, getEmailData } from '../actions';

// Styled Components
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;

  .not-linked-emails-table-header {
    font-size: 16px;
    font-weight: bold;
  }
`;

const ColumnSettingsButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background-color: #0056b3;
  }
`;

const OverlayContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 10px;
`;

const ColumnVisibilityOverlay = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 5px;
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 15px;
  min-width: 250px;
  z-index: 1000;

  .overlay-header {
    font-weight: bold;
    margin-bottom: 12px;
    color: #495057;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 8px;
  }

  .reset-button {
    background-color: #6c757d;
    color: white;
    border: none;
    padding: 4px 8px;
    border-radius: 3px;
    font-size: 12px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #5a6268;
    }
  }

  .column-toggles {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .column-toggle {
    display: flex;
    align-items: center;
    gap: 8px;

    input[type="checkbox"] {
      margin: 0;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    label {
      margin: 0;
      cursor: pointer;
      font-size: 14px;
      color: #495057;

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
`;

const OverlayBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
`;

const Button = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 10px;

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const ViewButton = styled.button`
  padding: 3px 6px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 5px;
`;

const EnvelopeButton = styled.button`
  padding: 3px 6px;
  background-color: #17a2b8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
`;

const ChildRow = styled.tr`
  background-color: #f8f9fa;

  td:first-child {
    border-left: 3px solid #007bff;
  }
`;

const TagSpan = styled.span`
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 2px 5px;
  margin: 2px;
  display: inline-block;
  background-color: #e9ecef;
  color: #495057;
  font-size: 12px;
`;

// Custom cell components for the email table
const ExpandCell = (props) => {
  const { row, isExpanded, onExpandRow, hasChildren } = props;
  const messageId = row.message_id;

  return (
    <td className="expand">
      {messageId && hasChildren(row) && (
        <i
          className={`fas fa-${isExpanded(messageId) ? 'minus' : 'plus'}-square`}
          title={isExpanded(messageId) ? 'Collapse' : 'Expand'}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            onExpandRow(row);
          }}
        />
      )}
    </td>
  );
};

const FromCell = ({ row }) => {
  const isEmailObject = typeof row === 'object';
  return <td className="from">{isEmailObject ? row.msg_from : row}</td>;
};

const ToCell = ({ row }) => {
  const isEmailObject = typeof row === 'object';
  return <td className="to">{isEmailObject ? row.msg_to : row}</td>;
};

const SubjectCell = ({ row }) => {
  const isEmailObject = typeof row === 'object';
  return <td className="subject">{isEmailObject ? row.msg_subject : row}</td>;
};

const EmailDateCell = ({ row }) => {
  const isEmailObject = typeof row === 'object';
  return (
    <td className="date">
      {isEmailObject ? moment(row.msg_date).format('MM/DD/YYYY hh:mm:ss A') : row}
    </td>
  );
};

const TagsCell = (props) => {
  const { row, onTagsClick } = props;
  const isEmailObject = typeof row === 'object';

  return (
    <td
      className="tags"
      onClick={(e) => isEmailObject && onTagsClick(e, row)}
      style={{ cursor: isEmailObject ? 'pointer' : 'default' }}
    >
      {isEmailObject && row.msg_tags && typeof row.msg_tags === 'string' ? (
        row.msg_tags.split(',').map((tag, i) => (
          <TagSpan key={`tag-${i}`}>{tag.trim()}</TagSpan>
        ))
      ) : (
        <span style={{ color: '#999' }}>click to edit tags</span>
      )}
    </td>
  );
};

const ActionsCell = (props) => {
  const { row, onViewEmail, onOpenEmailInOutlook } = props;
  const isEmailObject = typeof row === 'object';

  return (
    <td className="actions">
      {isEmailObject && row.msg_body ? (
        <>
          <ViewButton onClick={() => onViewEmail(row)}>
            View here
          </ViewButton>
          {row.weblink && (
            <EnvelopeButton
              onClick={() => onOpenEmailInOutlook(row)}
              title="View in Outlook"
            >
              <FontAwesomeIcon icon={faEnvelope} />
            </EnvelopeButton>
          )}
        </>
      ) : (
        <span style={{ color: '#999' }}>No content</span>
      )}
    </td>
  );
};

const NotLinkedEmailsTable = ({
  dispatch,
  notLinkedEmails,
  nextMessageId,
  isLoadingMore,
  onLoadMoreEmails,
  tagsFilterList,
  currentEmailData,
}) => {
  // Column visibility state and functions
  const getInitialColumnVisibility = () => {
    const defaultColumns = {
      from: true,
      to: false, // Hidden by default
      subject: true,
      date: true,
      tags: true,
      actions: true
    };

    try {
      const saved = localStorage.getItem('notLinkedEmailsTable_columnVisibility');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults to handle new columns
        return { ...defaultColumns, ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load column visibility settings:', error);
    }

    return defaultColumns;
  };

  const saveColumnVisibility = (visibleColumns) => {
    try {
      localStorage.setItem('notLinkedEmailsTable_columnVisibility', JSON.stringify(visibleColumns));
    } catch (error) {
      console.warn('Failed to save column visibility settings:', error);
    }
  };

  const [visibleColumns, setVisibleColumns] = useState(getInitialColumnVisibility());
  const [showColumnOverlay, setShowColumnOverlay] = useState(false);

  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isTagsOverlayOpen, setIsTagsOverlayOpen] = useState(false);
  const [selectedEmailForTags, setSelectedEmailForTags] = useState(null);
  const [workOrderList, setWorkOrderList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [childrenData, setChildrenData] = useState({});
  const [tableSort, setTableSort] = useState({
    field: 'date',
    direction: 'desc'
  });
  //const [loadingChildren, setLoadingChildren] = useState(new Set());

  const handleViewEmail = (email) => {
    setSelectedEmail(email);
  };

  const handleCloseOverlay = () => {
    setSelectedEmail(null);
  };

  const handleOpenTagsOverlay = (email) => {
    setSelectedEmailForTags(email);
    setIsTagsOverlayOpen(true);
    // Fetch email data when opening the modal
    dispatch(getEmailData(email.msg_id));
  };

  const handleCloseTagsOverlay = () => {
    setSelectedEmailForTags(null);
    setIsTagsOverlayOpen(false);
  };

  const handleOpenEmailInOutlook = (email) => {
    window.open(email.weblink, '_blank', 'noopener,noreferrer');
  }

  const handleSelectAssignee = (assignee) => {
    console.log('assignee', assignee);
    dispatch(selectAssigneeForEmail(selectedEmailForTags.msg_id, assignee));
  };

  const handleSelectTag = (value, checked) => {
    console.log('select tag', value, checked);
    if (checked) {
      dispatch(addTagForEmail(selectedEmailForTags.msg_id, value));
    } else {
      dispatch(removeTagForEmail(selectedEmailForTags.msg_id, value));
    }
  };

  const handleSearchWorkOrderList = (showNameOrWoMemfis, showCode, episodeNumber) => {
    dispatch(loadWorkOrderList({show_name: showNameOrWoMemfis, show_code: showCode, ep_num: episodeNumber})).then(res => {
      setWorkOrderList(res);
      setIsLoading(false);
    });
  };

  const handleTagsClick = (e, email) => {
    handleOpenTagsOverlay(email);
  };

  const handleExpandRow = async (email) => {
    const messageId = email.message_id;
    const newExpandedRows = new Set(expandedRows);

    if (expandedRows.has(messageId)) {
      // Collapse row
      newExpandedRows.delete(messageId);
      setExpandedRows(newExpandedRows);
    } else {
      // Expand row
      newExpandedRows.add(messageId);
      setExpandedRows(newExpandedRows);

      // Load children if not already loaded
      if (!childrenData[messageId]) {
        //const newLoadingChildren = new Set(loadingChildren);
        //newLoadingChildren.add(messageId);
        //setLoadingChildren(newLoadingChildren);

        //try {
          await dispatch(loadEmailChildren(messageId)).then(children => {
            setChildrenData(prev => ({
              ...prev,
              [messageId]: children || []
            }));
          });

        /*} catch (error) {
          console.error('Error loading children:', error);
          setChildrenData(prev => ({
            ...prev,
            [messageId]: []
          }));
        } finally {
          const updatedLoadingChildren = new Set(loadingChildren);
          updatedLoadingChildren.delete(messageId);
          setLoadingChildren(updatedLoadingChildren);
        }*/
      }
    }
  };

  // Sorting and table handlers
  const handleHeaderCellClick = (field, direction) => {
    if (field !== 'expand') {
      setTableSort({ field, direction });
    }
  };

  const handleRowClick = (row) => {
    const index = notLinkedEmails.findIndex(email =>
      email.message_id === row.message_id || email === row
    );
    setSelectedRowIndex(index);
  };

  const isExpanded = (messageId) => {
    return expandedRows.has(messageId);
  };

  const hasChildren = (email) => {
    const children = childrenData[email.message_id];
    return (children && children.references && children.references.length > 0) || email.has_references !== 0;
  };

  // Headers configuration
  const headers = {
    expand: {
      title: '',
      className: 'expand',
      component: ExpandCell,
      componentArgs: {
        isExpanded,
        onExpandRow: handleExpandRow,
        hasChildren
      }
    },
    from: {
      title: 'From',
      className: 'from',
      component: FromCell,
      componentArgs: {}
    },
    to: {
      title: 'To',
      className: 'to',
      component: ToCell,
      componentArgs: {}
    },
    subject: {
      title: 'Subject',
      className: 'subject',
      component: SubjectCell,
      componentArgs: {}
    },
    date: {
      title: 'Received On',
      className: 'date',
      component: EmailDateCell,
      componentArgs: {}
    },
    tags: {
      title: 'Tags',
      className: 'tags',
      component: TagsCell,
      componentArgs: {
        onTagsClick: handleTagsClick
      }
    },
    actions: {
      title: 'Actions',
      className: 'actions',
      component: ActionsCell,
      componentArgs: {
        onViewEmail: handleViewEmail,
        onOpenEmailInOutlook: handleOpenEmailInOutlook
      }
    }
  };

  const getVisibleHeaders = () => {
    const filteredHeaders = { expand: headers.expand }; // Always include expand column

    Object.keys(headers).forEach(key => {
      if (key !== 'expand' && visibleColumns[key]) {
        filteredHeaders[key] = headers[key];
      }
    });

    return filteredHeaders;
  };

  const sortAltFields = {
    date: 'msg_date',
    from: 'msg_from',
    to: 'msg_to',
    subject: 'msg_subject'
  };

  const renderRows = (emails) => {
    if (!emails) {
      return null;
    }

    const sortedEmails = getSortedRowsBy(emails, tableSort.field, tableSort.direction, sortAltFields);

    return sortedEmails.map((email, index) => {
      const isEmailObject = typeof email === 'object';
      const messageId = isEmailObject ? email.message_id : null;
      const isEmailExpanded = messageId && expandedRows.has(messageId);
      const children = messageId ? childrenData[messageId] : null;

      return (
        <React.Fragment key={`email-${index}`}>
          <Row
            headers={getVisibleHeaders()}
            row={email}
            onRowClick={handleRowClick}
            getRowClassNames={r => {
              const classes = [];
              const rowIndex = notLinkedEmails.findIndex(e =>
                e.message_id === r.message_id || e === r
              );
              if (selectedRowIndex === rowIndex) {
                classes.push('highlighted');
              }
              return classes.length > 0 ? classes.join(' ') : undefined;
            }}
          />

          {/* Render child rows if expanded */}
          {isEmailExpanded && children && children.references && children.references.length > 0 && (
            children.references.map((child, childIndex) => (
              <ChildRow key={`child-${messageId}-${childIndex}`}>
                <td></td>
                {visibleColumns.from && (
                  <td>{child.msg_from || 'N/A'}</td>
                )}
                {visibleColumns.to && (
                  <td>{child.msg_to || 'N/A'}</td>
                )}
                {visibleColumns.subject && (
                  <td>{child.msg_subject || 'N/A'}</td>
                )}
                {visibleColumns.date && (
                  <td className="date">
                    {child.msg_date ? moment(child.msg_date).format('MM/DD/YYYY hh:mm:ss A') : 'N/A'}
                  </td>
                )}
                {visibleColumns.tags && (
                  <td
                    className="tags"
                    onClick={(e) => handleTagsClick(e, child)}
                    style={{ cursor: 'pointer' }}
                  >
                    {child.msg_tags && typeof child.msg_tags === 'string' ? (
                      child.msg_tags.split(',').map((tag, i) => (
                        <TagSpan key={`child-tag-${i}`}>{tag.trim()}</TagSpan>
                      ))
                    ) : (
                      <span style={{ color: '#ccc' }}>No tags</span>
                    )}
                  </td>
                )}
                {visibleColumns.actions && (
                  <td className="actions">
                    {child.msg_body ? (
                      <>
                        <ViewButton onClick={() => handleViewEmail(child)}>
                          View
                        </ViewButton>
                        {child.weblink && (
                          <EnvelopeButton
                            onClick={() => handleOpenEmailInOutlook(child)}
                            title="View email content"
                          >
                            <FontAwesomeIcon icon={faEnvelope} />
                          </EnvelopeButton>
                        )}
                      </>
                    ) : (
                      <span style={{ color: '#ccc' }}>No content</span>
                    )}
                  </td>
                )}
              </ChildRow>
            ))
          )}

          {/* Show message when expanded but no children */}
          {isEmailExpanded && children && children.references && children.references.length === 0 && (
            <ChildRow>
              <td></td>
              <td colSpan={Object.values(visibleColumns).filter(Boolean).length} style={{ textAlign: 'center', padding: '10px' }}>
                No children items found
              </td>
            </ChildRow>
          )}
        </React.Fragment>
      );
    });
  };

  // Column visibility handlers
  const handleColumnToggle = (columnKey) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey]
    };

    // Prevent hiding all columns - at least one must remain visible
    const visibleCount = Object.values(newVisibleColumns).filter(Boolean).length;
    if (visibleCount === 0) {
      // Don't allow hiding the last visible column
      return;
    }

    // Save to localStorage
    saveColumnVisibility(newVisibleColumns);
    setVisibleColumns(newVisibleColumns);
  };

  const handleResetColumns = () => {
    const defaultColumns = {
      from: true,
      to: false,
      subject: true,
      date: true,
      tags: true,
      actions: true
    };

    saveColumnVisibility(defaultColumns);
    setVisibleColumns(defaultColumns);
  };

  const toggleColumnOverlay = () => {
    setShowColumnOverlay(!showColumnOverlay);
  };

  const closeColumnOverlay = () => {
    setShowColumnOverlay(false);
  };

  // Close overlay on Escape key
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showColumnOverlay) {
        closeColumnOverlay();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showColumnOverlay]);

  // Column labels for display
  const columnLabels = {
    from: 'From',
    to: 'To',
    subject: 'Subject',
    date: 'Received On',
    tags: 'Tags',
    actions: 'Actions'
  };

  // Calculate if this is the last visible column
  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length;

  return (
    <div className="not-linked-emails-container">
      <HeaderContainer>
        <div className="not-linked-emails-table-header">Orphaned Emails</div>
        <OverlayContainer>
          <ColumnSettingsButton onClick={toggleColumnOverlay}>
            <i className="fas fa-columns"></i>
            Columns
          </ColumnSettingsButton>

          {showColumnOverlay && (
            <>
              <OverlayBackdrop onClick={closeColumnOverlay} />
              <ColumnVisibilityOverlay>
                <div className="overlay-header">
                  <span>Column Visibility</span>
                  <button
                    className="reset-button"
                    onClick={handleResetColumns}
                    title="Reset to default columns"
                  >
                    Reset
                  </button>
                </div>
                <div className="column-toggles">
                  {Object.keys(columnLabels).map(columnKey => {
                    const isChecked = visibleColumns[columnKey];
                    const isLastVisible = isChecked && visibleColumnCount === 1;

                    return (
                      <div key={columnKey} className="column-toggle">
                        <input
                          type="checkbox"
                          id={`column-${columnKey}`}
                          checked={isChecked}
                          disabled={isLastVisible}
                          onChange={() => handleColumnToggle(columnKey)}
                          title={isLastVisible ? "Cannot hide the last visible column" : ""}
                        />
                        <label
                          htmlFor={`column-${columnKey}`}
                          className={isLastVisible ? 'disabled' : ''}
                        >
                          {columnLabels[columnKey]}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </ColumnVisibilityOverlay>
            </>
          )}
        </OverlayContainer>
      </HeaderContainer>

      <StyledTable
        className={classNames({
          'not-linked-emails-table': true,
          'table-wrapper': true,
        })}
      >
        <div>
          <div className="table-block">
            <table
              className={classNames({
                table: true,
                'n--table': true
              })}
            >
              <HeaderRow
                headers={getVisibleHeaders()}
                sort={tableSort}
                onHeaderCellClick={handleHeaderCellClick}
              />
              <tbody>
                {renderRows(notLinkedEmails)}
              </tbody>
              <caption>
                {!notLinkedEmails || notLinkedEmails.length === 0 ?
                  'No orphaned emails to display.' :
                  <span>&nbsp;</span>
                }
              </caption>
            </table>
            {nextMessageId && (
              <Button
                onClick={onLoadMoreEmails}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? 'Loading...' : 'Load more'}
              </Button>
            )}
          </div>
        </div>
      </StyledTable>

      {selectedEmail && (
          <ViewEmailContentModal email={selectedEmail} onClose={handleCloseOverlay} />
      )}

      {/* Tags Overlay */}
      {isTagsOverlayOpen && selectedEmailForTags && (
        <EmailTagsModal
          email={selectedEmailForTags}
          tagsFilterList={tagsFilterList}
          onClose={handleCloseTagsOverlay}
          onSelectAssignee={handleSelectAssignee}
          onSelectTag={handleSelectTag}
          isLoading={isLoading}
          workOrderList={workOrderList}
          onSearchWorkOrderList={handleSearchWorkOrderList}
          currentEmailData={currentEmailData}
        />
      )}
    </div>
  );
};

NotLinkedEmailsTable.propTypes = {
  notLinkedEmails: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        msg_subject: PropTypes.string.isRequired,
        msg_from: PropTypes.string,
        msg_to: PropTypes.string,
        msg_date: PropTypes.string,
        msg_body: PropTypes.string,
        msg_tags: PropTypes.string,
      }),
    ])
  ),
  nextMessageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoadingMore: PropTypes.bool,
  onLoadMoreEmails: PropTypes.func,
  tagsFilterList: PropTypes.object,
  currentEmailData: PropTypes.object,
};

NotLinkedEmailsTable.defaultProps = {
  notLinkedEmails: [],
  nextMessageId: null,
  isLoadingMore: false,
  onLoadMoreEmails: () => {},
  tagsFilterList: {},
  currentEmailData: {},
};

const mapStateToProps = (state) => {
  return {
    tagsFilterList: state.notLinkedItems.tagsFilterList,
    currentEmailData: state.notLinkedItems.currentEmailItem,
  };
};

export default connect(mapStateToProps)(NotLinkedEmailsTable);